import React, { Component, Fragment } from 'react';

import ColorPick from 'components/colorPick/ColorPick';
import { Form, FormControl } from 'components/form';
import InputToggle from 'components/input/toggle/InputToggle';
import { Loader } from 'components/loader';
import { I18n, translate } from 'components/translate';
import { get } from 'helpers/Object';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import { RGB_COLORS, TEMPLATES } from 'modules/configManager/constants';
import { ContentBasePage } from 'modules/content';
import { ENTITY_TYPE } from 'modules/file/constants';
import { UiService } from 'modules/ui';
import { PropTypes } from 'prop-types';

import ConfigCustomizationAsk from './ConfigCustomizationAsk';
import ConfigCustomizationImages from './ConfigCustomizationImages';
import TemplateSelector from './TemplateSelector';

import ConfigService from '../ConfigService';

import './ConfigCustomization.scss';

@translate()
@inject('ConfigStore', 'MembershipStore')
@observer
export default class ConfigCustomization extends Component {
  static propTypes = {
    ConfigStore: MobxPropTypes.objectOrObservableObject,
    MembershipStore: MobxPropTypes.objectOrObservableObject,
    fixedBarId: PropTypes.string,
    t: PropTypes.func
  };

  state = {
    showAsk: false,
    showLoader: true,
    showImageLoader: true,
    availableColors: []
  };

  verifyShowAsk() {
    const tokenAuthorities =
      window.localStorage && window.localStorage.__amplify__checkTokenData
        ? JSON.parse(window.localStorage.__amplify__checkTokenData)
        : {};
    const authorities =
      tokenAuthorities.data && tokenAuthorities.data.authorities
        ? tokenAuthorities.data.authorities
        : [];
    const authoritiesToShow = ['vendedor', 'atendente', 'produtor', 'admin'];
    const showAsk = authorities.some((role) =>
      authoritiesToShow.includes(role)
    );

    this.setState({ showAsk });
  }

  componentDidMount() {
    this.getData();
    this.verifyShowAsk();
  }

  async getData() {
    try {
      const customizationData = await ConfigService.fetchCustomizationData();

      if (!customizationData.template) {
        ConfigService.initTemplate();
      }

      await this.getColors();

      if (customizationData.membershipId) {
        this.getImages(customizationData.membershipId);
      }
    } catch (error) {
      UiService.setAlertError('error_try_again');
    }

    this.removeLoader();
  }

  async getImages(id) {
    try {
      const logoImageId = await ConfigService.getImage(
        id,
        ENTITY_TYPE.MEMBERSHIP_LOGO
      );
      const presentationImageId = await ConfigService.getImage(
        id,
        ENTITY_TYPE.MEMBERSHIP_PRESENTATION
      );
      const backgroundImageId = await ConfigService.getImage(
        id,
        ENTITY_TYPE.MEMBERSHIP_BACKGROUND
      );
      const backgroundAudioVideo = await ConfigService.getImage(
        id,
        ENTITY_TYPE.MEMBERSHIP_BACKGRD_AUDIO_VIDEO
      );

      ConfigService.setCustomizationData({
        images: {
          logoImageId,
          presentationImageId,
          backgroundImageId,
          backgroundAudioVideo
        }
      });
    } catch (error) {
      UiService.handleError(error);
    } finally {
      this.removeImageLoader();
    }
  }

  async getColors() {
    const availableColors = await ConfigService.fetchColors();

    this.setState({ availableColors });
  }

  removeLoader() {
    this.setState({ showLoader: false });
  }

  removeImageLoader() {
    this.setState({ showImageLoader: false });
  }

  addLoader() {
    this.setState({ showLoader: true });
  }

  getId(item) {
    if (!item || item.length === 0) {
      return null;
    }

    return item.fileId || item.id;
  }

  saveHandler = async (formData) => {
    this.addLoader();
    const { selectedTemplate } = this.props.ConfigStore.custom;

    const {
      logoImageId,
      presentationImageId,
      backgroundImageId,
      backgroundAudioVideo,
      autoSortModules
    } = formData;

    const data = {
      images: {
        logoImageId: this.getId(logoImageId),
        presentationImageId: this.getId(presentationImageId),
        backgroundImageId: this.getId(backgroundImageId),
        backgroundAudioVideo: backgroundAudioVideo
          ? backgroundAudioVideo.map((item) => this.getId(item))
          : []
      },
      template: selectedTemplate,
      autoSortModules
    };

    await ConfigService.updateCustomizationData(data);
    ConfigService.updateTemplate(selectedTemplate);
    ConfigService.setCustomizationData({ autoSortModules, selectedTemplate });
    this.updateImages(formData);
    this.removeLoader();
  };

  updateImages(formData) {
    const images = {
      logoImageId: [formData.logoImageId],
      presentationImageId: [formData.presentationImageId],
      backgroundImageId: [formData.backgroundImageId],
      backgroundAudioVideo: formData.backgroundAudioVideo
    };

    ConfigService.setCustomizationData({ images });
  }

  setTemplate = (selectedTemplate) => {
    ConfigService.setCustomizationData({
      selectedTemplate,
      template: selectedTemplate
    });
  };

  colorToRGB(color) {
    return RGB_COLORS[color];
  }

  rgbToColor(rgb) {
    return Object.keys(RGB_COLORS)[Object.values(RGB_COLORS).indexOf(rgb)];
  }

  colors(excludedColors = []) {
    let { availableColors } = this.state;

    availableColors = availableColors.filter(
      (color) => !excludedColors.includes(color)
    );
    const colors = availableColors
      ? availableColors.map((color) => this.colorToRGB(color))
      : [];

    return colors;
  }

  updatePrimaryColor = (color) => {
    const colorName = this.rgbToColor(color);

    ConfigService.setPrimaryColor(colorName);
  };

  updateSecondaryColor = (color) => {
    const colorName = this.rgbToColor(color);

    ConfigService.setSecondaryColor(colorName);
  };

  render() {
    const { ConfigStore, MembershipStore } = this.props;
    const { autoSortModules, lookAndFeel, secondaryColor, selectedTemplate } =
      ConfigStore.custom;
    const { showLoader, showImageLoader, showAsk } = this.state;
    const seletectedPrimaryColor = this.colorToRGB(lookAndFeel);
    const seletectedsecondaryColor = this.colorToRGB(secondaryColor);
    const { isOnlineEventTemplate } = MembershipStore;

    return (
      <Fragment>
        {showLoader ? (
          <Loader key="loader" />
        ) : (
          <Form onSubmit={this.saveHandler}>
            <div
              className="grid grid__block configcustomization"
              data-test="settings-customization-information"
            >
              <h2>
                <I18n label="config_manager.config_custom.title" />
              </h2>
              <span>
                <p>
                  <I18n label="config_manager.config_custom.info_description" />
                </p>
              </span>

              <label className="custom__options">
                <I18n label="config_manager.config_custom.module_numeration" />
              </label>

              <FormControl>
                <InputToggle
                  id="autoSortModules"
                  key="autoSortModules"
                  name="autoSortModules"
                  value={autoSortModules}
                  disabled={
                    get(selectedTemplate, 'template') !== TEMPLATES.DEFAULT
                  }
                  labelValue="config_manager.config_custom.module_numeration_description"
                  labelClass="secondary"
                  labelTooltip="config_manager.config_custom.module_numeration_tooltip"
                />
              </FormControl>

              {!isOnlineEventTemplate && (
                <TemplateSelector
                  onChange={this.setTemplate}
                  value={selectedTemplate}
                />
              )}

              <label className="custom__options">
                <I18n label="general.primary_color" />
              </label>
              <p className="secondary">
                <I18n label="config_manager.config_custom.primary_color_description" />
              </p>
              <ColorPick
                colors={this.colors([secondaryColor])}
                selectedColor={seletectedPrimaryColor}
                changeColor={this.updatePrimaryColor}
              />

              <label className="custom__options">
                <I18n label="general.secondary_color" />
              </label>
              <p className="secondary">
                <I18n label="config_manager.config_custom.secondary_color_description" />
              </p>
              <ColorPick
                colors={this.colors([lookAndFeel])}
                selectedColor={seletectedsecondaryColor}
                changeColor={this.updateSecondaryColor}
                resetColorAvailable
              />
            </div>

            {showAsk && <ConfigCustomizationAsk />}

            {showImageLoader ? (
              <Loader key="loader" />
            ) : (
              <ConfigCustomizationImages />
            )}

            <ContentBasePage.Footer onlySave isDisabled={showImageLoader} />
          </Form>
        )}
      </Fragment>
    );
  }
}
